import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, StatCard, rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, IndianRupee, TrendingDown, Wallet, Percent, ArrowUp, ArrowDown, Printer } from "lucide-react";
import { istNow, useIstDay } from "../lib/dates";

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };

function presetRange(key) {
  const t = istNow(); const s = (d) => fmt(d);
  if (key === "today") return [s(t), s(t)];
  if (key === "yesterday") { const y = new Date(t); y.setDate(y.getDate() - 1); return [s(y), s(y)]; }
  if (key === "week") return [s(startOfWeek(t)), s(t)];
  if (key === "month") return [s(new Date(t.getFullYear(), t.getMonth(), 1)), s(t)];
  if (key === "last_month") return [s(new Date(t.getFullYear(), t.getMonth() - 1, 1)), s(new Date(t.getFullYear(), t.getMonth(), 0))];
  return [s(new Date(t.getFullYear(), t.getMonth(), 1)), s(t)];
}
const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["last_month", "Last Month"], ["custom", "Custom"]];

function Delta({ v }) {
  if (v === null || v === undefined) return <span className="text-muted-foreground text-xs">—</span>;
  const up = v >= 0;
  return <span className={`text-xs inline-flex items-center gap-0.5 ${up ? "text-green-400" : "text-red-400"}`}>{up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(v)}%</span>;
}

function BreakdownCard({ title, items }) {
  const max = Math.max(...items.map((i) => i.amount), 1);
  return (
    <Card className="bg-card border-border p-4" data-testid={`ssp-breakdown-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="font-medium text-sm mb-3">{title}</p>
      <div className="space-y-2">
        {items.length ? items.map((i) => (
          <div key={i.key}>
            <div className="flex justify-between text-xs mb-1"><span>{i.label} <span className="text-muted-foreground">({i.count})</span></span><span className="text-primary">{rupee(i.amount)}</span></div>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary" style={{ width: `${(i.amount / max) * 100}%` }} /></div>
          </div>
        )) : <p className="text-xs text-muted-foreground">No data.</p>}
      </div>
    </Card>
  );
}

export default function ExpenseReport() {
  const navigate = useNavigate();
  const { branchParam } = useBranch();
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(presetRange("month")[0]);
  const [to, setTo] = useState(presetRange("month")[1]);
  const [category, setCategory] = useState("all");
  const [mode, setMode] = useState("all");
  const [addedBy, setAddedBy] = useState("all");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const setPresetRange = (k) => { setPreset(k); if (k !== "custom") { const [a, b] = presetRange(k); setFrom(a); setTo(b); } };

  const load = useCallback(() => {
    setLoading(true);
    const params = { start: from, end: to, branch_id: branchParam };
    if (category !== "all") params.category = category;
    if (mode !== "all") params.payment_mode = mode;
    if (addedBy !== "all") params.added_by = addedBy;
    api.get("/expenses/report", { params }).then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [from, to, branchParam, category, mode, addedBy]);
  useEffect(() => { load(); }, [load]);

  const addedByOptions = useMemo(() => [...new Set((d?.breakdown?.by_added_by || []).map((x) => x.key))], [d]);

  const exportCsv = () => {
    if (!d) return;
    const head = ["Date", "Branch", "Category", "Paid To", "Mode", "Reference", "Amount", "Added By", "Role"];
    const rows = d.rows.map((r) => [r.date, r.branch_name, r.category, r.paid_to || r.description || "-", r.payment_mode, r.reference_no, r.amount, r.created_by, r.created_by_role]);
    const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `expense_report_${from}_${to}.csv`; a.click();
  };
  const istDay = useIstDay();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (preset !== "custom") setPresetRange(preset); }, [istDay]);

  const s = d?.summary || { total_business: 0, total_expense: 0, net_balance: 0, expense_pct: null };

  return (
    <div data-testid="expense-report-page">
      <Button variant="ghost" className="mb-2 text-muted-foreground" onClick={() => navigate("/reports")} data-testid="er-back"><ChevronLeft size={16} className="mr-1" /> Reports</Button>
      <PageHeader title="Expense Report" subtitle="Business vs expense, net balance and breakdowns (reporting only)"
        actions={<div className="flex gap-2"><Button variant="outline" className="rounded-sm" onClick={exportCsv} data-testid="er-csv">CSV</Button><Button variant="outline" className="rounded-sm" onClick={() => window.print()} data-testid="er-print"><Printer size={15} className="mr-1" /> Print</Button></div>} />

      <Card className="p-4 bg-card border-border mb-6">
        <div className="flex flex-wrap gap-2 mb-3" data-testid="er-presets">
          {PRESETS.map(([k, label]) => (
            <Button key={k} size="sm" variant="ghost" onClick={() => setPresetRange(k)} data-testid={`er-preset-${k}`}
              className={`rounded-sm h-8 ${preset === k ? "bg-primary text-primary-foreground hover:bg-[#D4AF37]" : "bg-secondary"}`}>{label}</Button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="bg-secondary mt-1" data-testid="er-from" /></div>
          <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="bg-secondary mt-1" data-testid="er-to" /></div>
          <div><label className="text-xs text-muted-foreground">Category</label>
            <Select value={category} onValueChange={setCategory}><SelectTrigger className="bg-secondary mt-1" data-testid="er-category"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Categories</SelectItem>{(d?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><label className="text-xs text-muted-foreground">Payment Mode</label>
            <Select value={mode} onValueChange={setMode}><SelectTrigger className="bg-secondary mt-1" data-testid="er-mode"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Modes</SelectItem>{(d?.payment_modes || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><label className="text-xs text-muted-foreground">Added By</label>
            <Select value={addedBy} onValueChange={setAddedBy}><SelectTrigger className="bg-secondary mt-1" data-testid="er-addedby"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Everyone</SelectItem>{addedByOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={IndianRupee} label="Total Business" value={rupee(s.total_business)} testid="er-kpi-business" />
        <StatCard icon={TrendingDown} label="Total Expense" value={rupee(s.total_expense)} testid="er-kpi-expense" />
        <StatCard icon={Wallet} label="Net Balance" value={rupee(s.net_balance)} testid="er-kpi-net" />
        <StatCard icon={Percent} label="Expense %" value={s.expense_pct === null ? "—" : `${s.expense_pct}%`} testid="er-kpi-pct" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* last 3 months */}
        <Card className="bg-card border-border overflow-hidden">
          <p className="font-medium text-sm p-4 pb-2">Last 3 Months</p>
          <Table>
            <TableHeader><TableRow className="border-border"><TableHead>Month</TableHead><TableHead className="text-right">Business</TableHead><TableHead className="text-right">Expense</TableHead><TableHead className="text-right">Net Balance</TableHead></TableRow></TableHeader>
            <TableBody>
              {(d?.last_3_months || []).map((m) => (
                <TableRow key={m.month} className="border-border" data-testid={`er-m3-${m.month}`}>
                  <TableCell>{m.label}{m.partial && <Badge variant="outline" className="ml-1 text-[10px] border-primary text-primary">MTD</Badge>}</TableCell>
                  <TableCell className="text-right">{rupee(m.business)}</TableCell>
                  <TableCell className="text-right text-red-400">{rupee(m.expense)}</TableCell>
                  <TableCell className="text-right text-primary">{rupee(m.net_balance)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        {/* same-date comparison */}
        <Card className="bg-card border-border p-4" data-testid="er-samedate">
          <p className="font-medium text-sm mb-1">Same-Date Comparison</p>
          <p className="text-xs text-muted-foreground mb-3">{d?.same_date ? `Day 1–${d.same_date.day} of the month` : ""}</p>
          {d?.same_date && (
            <div className="space-y-3">
              {[["Business", "business"], ["Expense", "expense"], ["Net Balance", "net_balance"]].map(([label, k]) => (
                <div key={k} className="flex items-center justify-between text-sm border-b border-border pb-2">
                  <span className="text-muted-foreground w-24">{label}</span>
                  <span className="text-right">{d.same_date.current.label}: <span className="text-primary">{rupee(d.same_date.current[k])}</span></span>
                  <span className="text-right text-muted-foreground text-xs">{d.same_date.previous.label}: {rupee(d.same_date.previous[k])}</span>
                  <Delta v={d.same_date.pct[k]} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <BreakdownCard title="By Category" items={d?.breakdown?.by_category || []} />
        <BreakdownCard title="By Branch" items={d?.breakdown?.by_branch || []} />
        <BreakdownCard title="By Payment Mode" items={d?.breakdown?.by_payment_mode || []} />
        <BreakdownCard title="By Added By" items={d?.breakdown?.by_added_by || []} />
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="border-border"><TableHead>Date</TableHead><TableHead>Branch</TableHead><TableHead>Category</TableHead><TableHead>Paid To</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Added By</TableHead></TableRow></TableHeader>
          <TableBody>
            {(d?.rows || []).map((r) => (
              <TableRow key={r.id} className="border-border" data-testid={`er-row-${r.id}`}>
                <TableCell>{r.date}</TableCell>
                <TableCell className="text-xs">{r.branch_name}</TableCell>
                <TableCell><Badge variant="outline">{r.category}</Badge></TableCell>
                <TableCell className="text-muted-foreground" data-testid={`exp-paidto-${r.id}`}>{r.paid_to || r.description || "-"}</TableCell>
                <TableCell>{r.payment_mode}</TableCell>
                <TableCell className="text-right text-primary">{rupee(r.amount)}</TableCell>
                <TableCell className="text-xs">{r.created_by}<span className="block text-[11px] text-muted-foreground">{r.created_by_role}</span></TableCell>
              </TableRow>
            ))}
            {!(d?.rows || []).length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">{loading ? "Loading…" : "No expenses for this selection."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
